/* eslint-disable react/jsx-no-target-blank */
import React, { useEffect, useState } from 'react';
import {
    Container,
    TwoColumnsLayout,
    VideoColumn,
    Video,
    ContentColumn,
    Text,
    ConditionsContainer,
    ConditionsParagraph,
    ContentFooter,
    ButtonContainer,
    Header,
    ContainerWrapper,
    ConditionWrapper,
} from './TermsAndConditions.style';
import { withTranslation, Trans } from 'react-i18next';

import ShadowButton from 'components/shared/ShadowButton/ShadowButton';
import RoundedCheckbox from 'components/shared/RoundedCheckbox/RoundedCheckbox';
import poweredByNeurolyticsLogo from '../../assets/images/powered-by-neurolytics.png';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import withMediaQuery from '../../hoc/MediaQuery/withMediaQuery';
import CompanyLogo from '../../components/shared/CompanyLogo/CompanyLogo';
import {
    selectScanPreferencesError,
    selectScanPreferencesLoading,
} from '../../store/config/config.selectors';
import { storeScanPreferences } from '../../store/config/config.actions';
import { hasMentalTest } from '../../utils/ScanModulesDetection';
import {
    isOnlyCultureFit,
    isOnlyInterview,
} from '../../utils/module-identificators';

const TermsAndConditions = (props) => {
    const {
        t,
        companyName,
        language,
        introVideos,
        user,
        isTabletAndMobileSize,
        getScanModules,
        invitationId,
        assessmentId,
        onComplete,
        groupHasOptOutQuestionnaire,
        groupId,
    } = props;

    const { companySlug } = useParams();
    const dispatch = useDispatch();
    const publicScansConfig = useSelector(
        (state) => state.config.publicScansConfig,
    );
    const isScanPreferencesLoading = useSelector(selectScanPreferencesLoading);
    const hasScanPreferencesError = useSelector(selectScanPreferencesError);

    const [termsAccepted, setTermsAccepted] = useState(false);
    const [videoAccepted, setVideoAccepted] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const company = publicScansConfig[companySlug];
    const logoUrl =
        company && company.logoUrl
            ? company.logoUrl
            : 'https://s3.eu-west-1.amazonaws.com/neurolytics.public/neurolytics/images/neurolytics-logo.png';

    const privacyLinkS3 = {
        'nl-NL':
            'https://s3-eu-west-1.amazonaws.com/neurolytics.public/docs/neurolytics-privacy-statement_nl-NL.pdf',
        'en-US':
            'https://s3-eu-west-1.amazonaws.com/neurolytics.public/docs/neurolytics-privacy-statement_en-US.pdf',
    };

    const termsLinkS3 = {
        'nl-NL':
            'https://s3-eu-west-1.amazonaws.com/neurolytics.public/docs/neurolytics-terms-and-conditions_nl-NL.pdf',
        'en-US':
            'https://s3-eu-west-1.amazonaws.com/neurolytics.public/docs/neurolytics-terms-and-conditions_en-US.pdf',
    };
    const defaultIntroVideos = {
        'nl-NL':
            'https://s3-eu-west-1.amazonaws.com/neurolytics.public/neurolytics/videos/neurolytics-intro-video-nl.mp4',
        'en-US':
            'https://s3-eu-west-1.amazonaws.com/neurolytics.public/neurolytics/videos/neurolytics-intro-video-en.mp4',
    };

    let privacyLink = privacyLinkS3[language];
    let termsLink = termsLinkS3[language];
    let introVideoUrl =
        introVideos !== null
            ? introVideos[language]
            : defaultIntroVideos[language];

    useEffect(() => {
        if (
            submitted &&
            !isScanPreferencesLoading &&
            !hasScanPreferencesError
        ) {
            onComplete();
        }
    }, [submitted, isScanPreferencesLoading, hasScanPreferencesError]); // eslint-disable-line react-hooks/exhaustive-deps

    const onTermsCheck = (event) => {
        setTermsAccepted(event);
    };

    const onOptOutCheck = (checkEvent) => {
        setVideoAccepted(checkEvent);
    };

    const onContinue = () => {
        let isOptOut = !videoAccepted && !isScanOnlyInterview;
        if (isScanOnlyCultureFit && !groupHasOptOutQuestionnaire) {
            isOptOut = false;
        }
        dispatch(storeScanPreferences(assessmentId, invitationId, isOptOut));
        setSubmitted(true);
    };

    const scanModules = getScanModules();
    const scanModulesCount = scanModules.length;
    const isScanOnlyInterview = isOnlyInterview(scanModules);
    const isScanOnlyCultureFit = isOnlyCultureFit(scanModules);
    const videoScanIds = ['culture-fit', 'speaking', 'mental-test'];
    const videoScanSteps = scanModules.filter((module) =>
        videoScanIds.includes(module.id),
    );
    const renderStepLabels = (steps) => {
        return steps.map((module, index) => {
            const isLast = index === steps.length - 1;
            const isSecondToLast = index === steps.length - 2;

            return (
                <span key={module.id}>
                    {module.label}
                    {!isLast
                        ? isSecondToLast
                            ? ` ${language === 'nl-NL' ? 'en' : 'and'} `
                            : ', '
                        : ''}
                </span>
            );
        });
    };

    return (
        <ContainerWrapper>
            <Container>
                <Header>
                    <CompanyLogo logoUrl={logoUrl} />
                </Header>
                <TwoColumnsLayout>
                    {isTabletAndMobileSize && (
                        <ContentColumn>
                            <Text>
                                <h1>
                                    {t('TERMS_AND_CONDITIONS.TITLE', {
                                        user_first_name: user.given_name,
                                    })}
                                </h1>
                                <h2>
                                    {t('TERMS_AND_CONDITIONS.SUBTITLE', {
                                        company_name: companyName,
                                    })}
                                </h2>
                                <p>{t('TERMS_AND_CONDITIONS.MESSAGE1')}</p>
                                <p>
                                    {t('TERMS_AND_CONDITIONS.MESSAGE2', {
                                        scan_modules_number: scanModulesCount,
                                    })}
                                    {scanModulesCount === 1 &&
                                        t('TERMS_AND_CONDITIONS.STEP')}
                                    {scanModulesCount > 1 &&
                                        t('TERMS_AND_CONDITIONS.STEPS')}
                                    :
                                </p>
                                <br />
                                <ul>
                                    {scanModules.map((module) => (
                                        <li key={module.id}>{module.label}</li>
                                    ))}
                                </ul>

                                {videoScanSteps.length > 0 &&
                                    companySlug !== 'bunq' &&
                                    groupId !==
                                        '156a6deb-bea8-4c24-a17d-3ac837458426' &&
                                    groupId !==
                                        '263f77aa-b140-4c20-a4bf-4ad7f3581f75' && (
                                        <p>
                                            {t(
                                                'TERMS_AND_CONDITIONS.SUB_MESSAGE3',
                                            )}
                                            {renderStepLabels(videoScanSteps)}
                                            {videoScanSteps.length === 1 &&
                                                t('TERMS_AND_CONDITIONS.STEP')}
                                            {videoScanSteps.length > 1 &&
                                                t('TERMS_AND_CONDITIONS.STEPS')}
                                            {t('TERMS_AND_CONDITIONS.MESSAGE3')}
                                        </p>
                                    )}
                            </Text>
                        </ContentColumn>
                    )}
                    {companySlug !== 'bunq' &&
                        groupId !== '156a6deb-bea8-4c24-a17d-3ac837458426' &&
                        groupId !== '263f77aa-b140-4c20-a4bf-4ad7f3581f75' && (
                            <VideoColumn>
                                <Video
                                    src={introVideoUrl}
                                    controls
                                    controlsList="nodownload"
                                />
                            </VideoColumn>
                        )}
                    {isTabletAndMobileSize && (
                        <ContentColumn>
                            <ConditionsContainer>
                                <ConditionWrapper>
                                    <RoundedCheckbox
                                        id="acceptConditions"
                                        onChange={onTermsCheck}
                                    />
                                    <ConditionsParagraph>
                                        <Trans i18nKey="TERMS_AND_CONDITIONS.TERMS_AGREEMENT_TEXT">
                                            I have read and agree with the
                                            <a
                                                href={privacyLink}
                                                target="_blank"
                                                rel="noreferrer">
                                                Privacy Statement
                                            </a>
                                            and
                                            <a
                                                href={termsLink}
                                                target="_blank"
                                                rel="noreferrer">
                                                Terms and Conditions
                                            </a>
                                        </Trans>
                                    </ConditionsParagraph>
                                </ConditionWrapper>
                                <br />
                                {videoScanSteps.length > 0 &&
                                    !hasMentalTest(scanModules) &&
                                    !isScanOnlyInterview &&
                                    !isScanOnlyCultureFit &&
                                    companySlug !== 'bunq' &&
                                    groupId !==
                                        '156a6deb-bea8-4c24-a17d-3ac837458426' &&
                                    groupId !==
                                        '263f77aa-b140-4c20-a4bf-4ad7f3581f75' && (
                                        <ConditionWrapper>
                                            <RoundedCheckbox
                                                id="optOutCondition"
                                                onChange={onOptOutCheck}
                                            />
                                            <ConditionsParagraph>
                                                <Trans i18nKey="TERMS_AND_CONDITIONS.VIDEO_AGREEMENT_TEXT"></Trans>
                                            </ConditionsParagraph>
                                        </ConditionWrapper>
                                    )}
                                {videoScanSteps.length > 0 &&
                                    !hasMentalTest(scanModules) &&
                                    isScanOnlyCultureFit &&
                                    groupHasOptOutQuestionnaire &&
                                    companySlug !== 'bunq' &&
                                    groupId !==
                                        '156a6deb-bea8-4c24-a17d-3ac837458426' &&
                                    groupId !==
                                        '263f77aa-b140-4c20-a4bf-4ad7f3581f75' && (
                                        <ConditionWrapper>
                                            <RoundedCheckbox
                                                id="optOutCondition"
                                                onChange={onOptOutCheck}
                                            />
                                            <ConditionsParagraph>
                                                <Trans i18nKey="TERMS_AND_CONDITIONS.VIDEO_AGREEMENT_TEXT"></Trans>
                                            </ConditionsParagraph>
                                        </ConditionWrapper>
                                    )}
                            </ConditionsContainer>
                        </ContentColumn>
                    )}
                    {!isTabletAndMobileSize && (
                        <ContentColumn>
                            <Text>
                                <h1>
                                    {t('TERMS_AND_CONDITIONS.TITLE', {
                                        user_first_name: user.given_name,
                                    })}
                                </h1>
                                <h2>
                                    {t('TERMS_AND_CONDITIONS.SUBTITLE', {
                                        company_name: companyName,
                                    })}
                                </h2>
                                <p>{t('TERMS_AND_CONDITIONS.MESSAGE1')}</p>
                                <p>
                                    {t('TERMS_AND_CONDITIONS.MESSAGE2', {
                                        scan_modules_number: scanModulesCount,
                                    })}
                                    {scanModulesCount === 1 &&
                                        t('TERMS_AND_CONDITIONS.STEP')}
                                    {scanModulesCount > 1 &&
                                        t('TERMS_AND_CONDITIONS.STEPS')}
                                    :
                                </p>
                                <br />
                                <ul>
                                    {scanModules.map((module) => (
                                        <li key={module.id}>{module.label}</li>
                                    ))}
                                </ul>

                                {videoScanSteps.length > 0 &&
                                    companySlug !== 'bunq' &&
                                    groupId !==
                                        '156a6deb-bea8-4c24-a17d-3ac837458426' &&
                                    groupId !==
                                        '263f77aa-b140-4c20-a4bf-4ad7f3581f75' && (
                                        <p>
                                            {t(
                                                'TERMS_AND_CONDITIONS.SUB_MESSAGE3',
                                            )}
                                            {renderStepLabels(videoScanSteps)}
                                            {videoScanSteps.length === 1 &&
                                                t('TERMS_AND_CONDITIONS.STEP')}
                                            {videoScanSteps.length > 1 &&
                                                t('TERMS_AND_CONDITIONS.STEPS')}
                                            {t('TERMS_AND_CONDITIONS.MESSAGE3')}
                                        </p>
                                    )}
                            </Text>
                            <ConditionsContainer>
                                <ConditionWrapper>
                                    <RoundedCheckbox
                                        id="acceptConditions"
                                        onChange={onTermsCheck}
                                    />
                                    <ConditionsParagraph>
                                        <Trans i18nKey="TERMS_AND_CONDITIONS.TERMS_AGREEMENT_TEXT">
                                            I have read and agree with the
                                            <a
                                                href={privacyLink}
                                                target="_blank"
                                                rel="noreferrer">
                                                Privacy Statement
                                            </a>
                                            and
                                            <a
                                                href={termsLink}
                                                target="_blank"
                                                rel="noreferrer">
                                                Terms and Conditions
                                            </a>
                                        </Trans>
                                    </ConditionsParagraph>
                                </ConditionWrapper>
                                <br />
                                {videoScanSteps.length > 0 &&
                                    !isScanOnlyInterview &&
                                    !isScanOnlyCultureFit &&
                                    companySlug !== 'bunq' &&
                                    groupId !==
                                        '156a6deb-bea8-4c24-a17d-3ac837458426' &&
                                    groupId !==
                                        '263f77aa-b140-4c20-a4bf-4ad7f3581f75' && (
                                        <ConditionWrapper>
                                            <RoundedCheckbox
                                                id="optOutCondition"
                                                onChange={onOptOutCheck}
                                            />
                                            <ConditionsParagraph>
                                                <Trans i18nKey="TERMS_AND_CONDITIONS.VIDEO_AGREEMENT_TEXT"></Trans>
                                            </ConditionsParagraph>
                                        </ConditionWrapper>
                                    )}
                                {videoScanSteps.length > 0 &&
                                    isScanOnlyCultureFit &&
                                    groupHasOptOutQuestionnaire &&
                                    companySlug !== 'bunq' &&
                                    groupId !==
                                        '156a6deb-bea8-4c24-a17d-3ac837458426' &&
                                    groupId !==
                                        '263f77aa-b140-4c20-a4bf-4ad7f3581f75' && (
                                        <ConditionWrapper>
                                            <RoundedCheckbox
                                                id="optOutCondition"
                                                onChange={onOptOutCheck}
                                            />
                                            <ConditionsParagraph>
                                                <Trans i18nKey="TERMS_AND_CONDITIONS.VIDEO_AGREEMENT_TEXT"></Trans>
                                            </ConditionsParagraph>
                                        </ConditionWrapper>
                                    )}
                            </ConditionsContainer>
                            <ContentFooter>
                                {hasScanPreferencesError && (
                                    <Text className="error">
                                        <p>{t('TERMS_AND_CONDITIONS.ERROR')}</p>
                                    </Text>
                                )}
                                <ButtonContainer>
                                    <ShadowButton
                                        disabled={
                                            !termsAccepted ||
                                            isScanPreferencesLoading ||
                                            hasScanPreferencesError
                                        }
                                        onClick={onContinue}>
                                        {t(
                                            'TERMS_AND_CONDITIONS.CONTINUE_BUTTON',
                                        )}
                                    </ShadowButton>
                                </ButtonContainer>
                            </ContentFooter>
                        </ContentColumn>
                    )}
                </TwoColumnsLayout>
                {isTabletAndMobileSize && (
                    <>
                        <div className="spacer"></div>
                        <ContentFooter>
                            {hasScanPreferencesError && (
                                <Text className="error">
                                    <p>{t('TERMS_AND_CONDITIONS.ERROR')}</p>
                                </Text>
                            )}
                            <ButtonContainer>
                                <ShadowButton
                                    size={'small'}
                                    disabled={
                                        !termsAccepted ||
                                        isScanPreferencesLoading ||
                                        hasScanPreferencesError
                                    }
                                    onClick={onContinue}>
                                    {t('TERMS_AND_CONDITIONS.CONTINUE_BUTTON')}
                                </ShadowButton>
                            </ButtonContainer>
                        </ContentFooter>
                    </>
                )}
                <img
                    className="power-logo"
                    src={poweredByNeurolyticsLogo}
                    alt="Neurolytics Logo"
                />
            </Container>
        </ContainerWrapper>
    );
};

export default withMediaQuery(withTranslation()(TermsAndConditions));
